/**
 * Mask a child's name for on-screen display.
 *
 * WHY. A therapy centre's screens are on shared desks, projected in team meetings
 * and shoulder-surfed in waiting areas, and a child's full name next to their
 * clinical record is the most identifying thing on the page. Masking keeps a name
 * recognisable to the person who already knows the child - enough to pick the right
 * card out of a grid - while not handing it to whoever is standing behind them.
 *
 * WHAT THIS IS NOT. It is a DISPLAY transform, not access control. The real name is
 * still in the API response, still in the browser's memory, and still in the network
 * tab. Anyone who can open the app can recover it. Treat this as a shoulder-surfing
 * and screenshot measure, not as a reason to widen who may open a child's record -
 * that is what the permission catalogue is for.
 *
 * THE RULE: keep the first THREE and the last character of each word, star the middle.
 *
 *   "Chaaya Kumar"   -> "Cha**a Kum*r"
 *   "CLIENT T"       -> "CLI**T T"
 *   "DEVELOPER TEST" -> "DEV*****R TE*T"
 *   "ABC ABC"        -> "A*C A*C"
 *
 * WHY THREE AND NOT ONE. A single leading letter was too little to work with: a grid of
 * "C****a", "C****e" and "C***i" is no faster to scan than a grid of client codes, which
 * defeats the point of showing a name at all. Three letters is enough to tell children
 * apart at a glance and still short of the name itself.
 *
 * AT LEAST ONE CHARACTER IS ALWAYS HIDDEN. The lead is capped at (length - 2) so a short
 * word can never be revealed in full: "Sam" stays "S*m" rather than becoming "Sam". That
 * cap is what keeps this a mask rather than a formality.
 *
 * Words of one or two characters are left alone: there is no middle to star, and
 * turning "T" into "*" would lose the initial that makes two children with the same
 * surname distinguishable at a glance.
 *
 * The star run is the real length of the hidden middle, deliberately. A fixed-width
 * mask makes "Jo" and "Jonathan" look alike and makes a list of children harder to
 * scan than it needs to be; the length of a name is not the identifying part.
 */

/** A run of characters that is not whitespace - kept as-is so hyphens and dots survive. */
const WORD = /\S+/g;

/** How many leading characters stay readable. See "WHY THREE AND NOT ONE" above. */
const VISIBLE_LEAD = 3;

/**
 * Mask one word: the first few characters, a star per hidden character, the last one.
 *
 * The lead is capped at `length - 2` so there is always at least one starred character
 * between the lead and the final letter - without that cap, "Sam" would come back as
 * "Sam". Short words (<= 2) are returned unchanged; see the note above on why.
 */
const maskWord = (word) => {
  if (word.length <= 2) return word;
  const lead = Math.min(VISIBLE_LEAD, word.length - 2);
  return `${word.slice(0, lead)}${'*'.repeat(word.length - lead - 1)}${word[word.length - 1]}`;
};

/**
 * @param {string} name - the child's name as stored
 * @returns {string} the masked name, or '' for a missing one
 */
export const maskChildName = (name) => {
  const text = String(name ?? '').trim();
  if (!text) return '';
  return text.replace(WORD, maskWord);
};

/**
 * Mask only the name inside a "CODE (Name)" label, leaving the code readable.
 *
 * The client code is the identifier people actually work from - it is on the card,
 * in the URL and in every report - and it identifies nobody on its own, so masking
 * it would cost the whole point of showing a label while protecting nothing.
 *
 *   "CLT-20260511-LVKX (CLIENT T)" -> "CLT-20260511-LVKX (C****T T)"
 */
export const maskChildLabel = (label) => {
  const text = String(label ?? '').trim();
  if (!text) return '';
  const match = text.match(/^(.*?)\s*\(([^)]*)\)\s*$/);
  if (!match) return maskChildName(text);
  const [, code, name] = match;
  const masked = maskChildName(name);
  return code ? `${code} (${masked})` : `(${masked})`;
};

export default maskChildName;
